// @flow
/* eslint-disable no-console */
import {
  post as createPaymentIntent,
  PaymentIntentCreationError,
} from '@rover/react-lib/apis/paymentIntent.api';

import inject from '@rover/utilities/injector';
import { loadScript } from './script';

export const STRIPE_JS_TAG_ID = 'stripe-js';
export const STRIPE_JS_URL = 'https://js.stripe.com/v3/';
export const WEBKIT_SUCCESS_CALLBACK = 'webkitAuthenticatePaymentIntentSuccessful';
export const WEBKIT_FAILURE_CALLBACK = 'webkitAuthenticatePaymentIntentFailure';

export class PaymentIntentAuthenticationError extends Error {
  type: string = 'PaymentIntentAuthenticationError';
}

// Put the inject in a function so STRIPE_PUBLIC_KEY does not need to be a dependency of this module at load time
const getStripeApiKey = () => inject('Rover.context.STRIPE_PUBLIC_KEY');

// Use module because we cannot mock individual function otherwise due to the way module is being loaded
const module = {};
type MessageHandlerType = {
  (any): void,
  postMessage: any => void,
};

module.shouldAuthenticateWithIOSSDK = () => inject('Rover.context.useStripeNativeIosSdk', false);

module.getWebkitMessageHandler = (): MessageHandlerType =>
  window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.rover;

module.loadStripe = async (): any => {
  if (window && window.Stripe) {
    return window.Stripe;
  }
  await loadScript(STRIPE_JS_URL, STRIPE_JS_TAG_ID);
  return window.Stripe;
};

module.getStripeClient = async (apiKey?: string): any => {
  const publicKey = getStripeApiKey();
  const Stripe = await module.loadStripe();
  return Stripe(apiKey || publicKey);
};

module.authenticatePaymentIntent = async (
  clientSecret: string,
  requiresAction: boolean,
  opk: string
): Promise<string> => {
  if (requiresAction) {
    if (module.shouldAuthenticateWithIOSSDK() && module.getWebkitMessageHandler()) {
      console.debug('Authenticate payment intent with webkit');
      return module.authenticatePaymentIntentIOS(clientSecret, opk);
    }
    console.debug('Authenticate payment intent with stripejs');
    return module.authenticatePaymentIntentStripeJS(clientSecret, opk);
  }
  return opk;
};

module.authenticatePaymentIntentStripeJS = async (
  clientSecret: string,
  opk: string
): Promise<string> => {
  const stripeClient = await module.getStripeClient();
  let result = {};
  try {
    result = await stripeClient.handleCardAction(clientSecret);
  } catch (e) {
    throw new PaymentIntentAuthenticationError();
  }
  if (result.error) {
    throw new PaymentIntentAuthenticationError(result.error.message);
  }
  return opk;
};

/** This function delegates payment intent authentication to iOS native SDK
 * and exposes global callbacks that allows iOS to communicate authentication status back to the embedded web
 */
module.authenticatePaymentIntentIOS = async (
  clientSecret: string,
  opk: string
): Promise<string> => {
  const resultPromise = new Promise((resolve, reject) => {
    window[WEBKIT_SUCCESS_CALLBACK] = () => resolve(opk);
    window[WEBKIT_FAILURE_CALLBACK] = () => reject();
  });
  let result;
  module.getWebkitMessageHandler().postMessage({
    action: 'stripe-challenge',
    clientSecret,
    stripeApiKey: getStripeApiKey(),
    successJSCallback: `window.${WEBKIT_SUCCESS_CALLBACK}`,
    errorJSCallback: `window.${WEBKIT_FAILURE_CALLBACK}`,
    closeJSCallback: `window.${WEBKIT_FAILURE_CALLBACK}`,
  });
  try {
    result = await resultPromise;
  } catch (e) {
    throw new PaymentIntentAuthenticationError();
  }
  window.webkitAuthenticatePaymentIntentSuccessful = undefined;
  window.webkitAuthenticatePaymentIntentFailure = undefined;
  return result;
};

export default module;
export const { loadStripe, getStripeClient, authenticatePaymentIntent } = module;
export { PaymentIntentCreationError, createPaymentIntent };



// WEBPACK FOOTER //
// ../utilities/stripe.client.js